import { useCallback, useMemo, useState } from 'react';

import { FILL_LATER_LEAD_TIME_DAYS } from 'common/constants/fillLater';
import {
  addDaysIso,
  isRealCalendarDay,
  partitionBySchedule,
  scheduleProblem,
  todayIso,
} from '../utils/fillLaterSchedule';

/**
 * The fill later schedule the Bulk Update apply bar stamps across a selection.
 *
 * A LIST of `{ quantity, scheduledFor }`, not one date, as NDC_UPDATE carries one productId: the
 * operator builds the whole schedule once and pushes it onto every selected order (POM-6167 —
 * split in bulk, instead of one at a time from the patient profile). Each row then holds its own
 * copy, so adjusting one order afterwards cannot edit the bar or its siblings.
 *
 * Its own hook rather than more state in useBulkOverride: that file sits at the `max-lines`
 * ceiling, and the schedule is a self-contained concern with one input (which rows are selected)
 * and one output (what a commit would send).
 */

/* The lead time is a SUGGESTION, not a floor (POM-6197). Each line opens on the configured number
 * of days past the line before it, so the common case — evenly spaced fills — needs no typing at
 * all, while every one of those dates stays editable down to tomorrow. */
const prefilledLine = (previousDate) => {
  const base = isRealCalendarDay(previousDate) ? previousDate : todayIso();

  return { quantity: '', scheduledFor: addDaysIso(base, FILL_LATER_LEAD_TIME_DAYS) };
};

const prefilledMoveDate = () => addDaysIso(todayIso(), FILL_LATER_LEAD_TIME_DAYS);

const useFillLaterSchedule = (params) => {
  const { isActive, rows, selectedIds, stagedValues, dispenseQuantity } = params;

  /* One prefilled line to start, so the bar always has a row to type a quantity into. */
  const [schedule, setSchedule] = useState(() => [prefilledLine()]);
  const [moveDate, setMoveDate] = useState(prefilledMoveDate);

  const changeScheduleLine = useCallback((lineParams) => {
    const { index, field, value } = lineParams;

    setSchedule((current) =>
      current.map((line, at) => (at === index ? { ...line, [field]: value } : line))
    );
  }, []);

  /* Chains off the last line's raw date, not the last COMPLETE one: a line that has its prefilled
   * date but no quantity yet is incomplete, and basing the next date on complete lines only would
   * hand out the same day twice. */
  const addScheduleLine = useCallback(
    () =>
      setSchedule((current) => [
        ...current,
        prefilledLine(current[current.length - 1]?.scheduledFor),
      ]),
    []
  );

  /* Never removes the last line: an empty bar has nothing to type into, and the operator would
   * then have to find an "add" control to get back to where they started. */
  const removeScheduleLine = useCallback(
    (index) =>
      setSchedule((current) =>
        current.length <= 1 ? [prefilledLine()] : current.filter((_line, at) => at !== index)
      ),
    []
  );

  const clearSchedule = useCallback(() => {
    setSchedule([prefilledLine()]);
    setMoveDate(prefilledMoveDate());
  }, []);

  const moveDateIssue = useMemo(() => {
    if (!isRealCalendarDay(moveDate)) return { message: 'Select a fill date' };

    return moveDate <= todayIso() ? { message: 'Fill dates must be after today' } : null;
  }, [moveDate]);

  /* The schedule's own soundness, independent of any row: since POM-6197 that is one rule, every
   * date after today. Checked before a stamp, so the bar refuses to push a schedule the server
   * would reject on every row it touched. */
  const scheduleIssue = useMemo(
    () => (isActive ? scheduleProblem({ schedule, dispenseQuantity }) : null),
    [isActive, schedule, dispenseQuantity]
  );

  /* Which selected rows a commit would actually send. One answer for the footer tally, the
   * confirm summary and the payload, so the three cannot disagree about the count. */
  const scheduleBreakdown = useMemo(() => {
    if (!isActive) return { ready: [], blocked: [], unscheduled: [] };

    return partitionBySchedule({
      rows: (rows || []).filter((row) => selectedIds.includes(row.lineItemId)),
      stagedValues,
    });
  }, [isActive, rows, selectedIds, stagedValues]);

  return {
    schedule,
    scheduleIssue,
    scheduleBreakdown,
    moveDate,
    moveDateIssue,
    changeMoveDate: setMoveDate,
    changeScheduleLine,
    addScheduleLine,
    removeScheduleLine,
    clearSchedule,
  };
};

export default useFillLaterSchedule;
